import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Container, Form, Input, Button, Label, Alert, FormGroup } from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import sendRequest, { setAuthToken } from '../../helpers/axios-setup';

const GeneralSettings = () => {
    const [breadcrumbItems] = useState([
        { title: 'Settings', link: '#' },
        { title: 'Configuration', link: '#' },
    ]);

    // Existing settings state
    const [settings, setSettings] = useState([]);
    const [selectedSetting, setSelectedSetting] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [cronTime, setCronTime] = useState('');
    const [hasCron, setHasCron] = useState(false);
    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Shop Version state
    const [shopVersion, setShopVersion] = useState(null); // { version: string } or null
    const [shopVersionInput, setShopVersionInput] = useState('');
    const [shopVersionLoading, setShopVersionLoading] = useState(false);
    const [shopVersionUpdating, setShopVersionUpdating] = useState(false);
    const [shopVersionMessage, setShopVersionMessage] = useState({ type: '', text: '' });

    // Semver regex for validation (optional but helpful)
    const semverRegex = /^\d+\.\d+\.\d+$/;

    useEffect(() => {
        fetchSettings();
        fetchShopVersion();
    }, []);

    // Fetch all settings for dropdown
    async function fetchSettings() {
        setLoading(true);
        try {
            setAuthToken();
            const { data } = await sendRequest.get('/settings');
            setSettings(data);
        } catch (error) {
            console.error('Error fetching settings:', error);
            setMessage({
                type: 'danger',
                text: 'Failed to load settings. Please try again.'
            });
        }
        setLoading(false);
    }

    // Fetch shop version
    async function fetchShopVersion() {
        setShopVersionLoading(true);
        try {
            setAuthToken();
            const response = await sendRequest.get('/settings/version');
            console.log(response);
            // API returns { success: true, shopVersion: object or null }
            if (response.data.shopVersion) {
                setShopVersion(response.data.shopVersion);
                setShopVersionInput(response.data.shopVersion.version);
            } else {
                setShopVersion(null);
                setShopVersionInput('');
            }
            setShopVersionMessage({ type: '', text: '' });
        } catch (error) {
            console.error('Error fetching shop version:', error);
            setShopVersionMessage({
                type: 'danger',
                text: 'Failed to load shop version.'
            });
        }
        setShopVersionLoading(false);
    }

    // Handle setting selection from dropdown
    const handleSettingSelect = (settingId) => {
        const setting = settings.find(s => s._id === settingId);
        if (setting) {
            setSelectedSetting(settingId);
            setAdminEmail(setting.admin_email || '');
            setCronTime(setting.cron_time || '0 8 * * *');
            setHasCron(setting.hasCron || false);
        }
    };

    // Reset settings form fields
    const resetForm = () => {
        setSelectedSetting('');
        setAdminEmail('');
        setCronTime('');
        setHasCron(false);
    };

    // Handle settings form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!selectedSetting) {
            setMessage({
                type: 'danger',
                text: 'Please select a setting to update.'
            });
            return;
        }

        setUpdating(true);
        setMessage({ type: '', text: '' });

        try {
            setAuthToken();
            await sendRequest.put(`/settings/${selectedSetting}`, {
                admin_email: adminEmail,
                cron_time: cronTime,
                hasCron: hasCron
            });

            setMessage({
                type: 'success',
                text: 'Settings updated successfully!'
            });

            resetForm();
            fetchSettings();
        } catch (error) {
            console.error('Error updating settings:', error);
            setMessage({
                type: 'danger',
                text: 'Failed to update settings. Please try again.'
            });
        }
        setUpdating(false);
    };

    // Handle shop version submission (create or update)
    const handleShopVersionSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!shopVersionInput.trim()) {
            setShopVersionMessage({
                type: 'danger',
                text: 'Version is required.'
            });
            return;
        }
        if (!semverRegex.test(shopVersionInput)) {
            setShopVersionMessage({
                type: 'danger',
                text: 'Version must be in semver format (e.g., 1.0.0).'
            });
            return;
        }

        setShopVersionUpdating(true);
        setShopVersionMessage({ type: '', text: '' });

        try {
            setAuthToken();
            if (shopVersion) {
                // Update existing
                await sendRequest.put('/settings/version/update', { version: shopVersionInput });
            } else {
                // Create new
                await sendRequest.post('/settings/version', { version: shopVersionInput });
            }

            setShopVersionMessage({
                type: 'success',
                text: `Shop version ${shopVersion ? 'updated' : 'created'} successfully!`
            });

            // Refresh shop version
            await fetchShopVersion();
        } catch (error) {
            console.error('Error saving shop version:', error);
            setShopVersionMessage({
                type: 'danger',
                text: error.response?.data?.message || 'Failed to save shop version.'
            });
        }
        setShopVersionUpdating(false);
    };

    // Cron time format helper text
    const cronFormatHelper = "Cron format: minute hour day month dayOfWeek (e.g., '0 8 * * *' for 8:00 AM daily)";

    return (
        <React.Fragment>
            <div className='page-content'>
                <Container fluid>
                    <Breadcrumbs title='Settings Configuration' breadcrumbItems={breadcrumbItems} />

                    {/* Settings Configuration Section */}
                    <Row>
                        <Col lg={8}>
                            <Card>
                                <CardBody>
                                    <h4 className='card-title mb-4'>Update Settings</h4>

                                    {message.text && (
                                        <Alert color={message.type} className="mb-3">
                                            {message.text}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handleSubmit}>
                                        {/* Setting Selection Dropdown */}
                                        <div className='mb-3'>
                                            <Label className='form-label'>Select Setting</Label>
                                            <Input
                                                type='select'
                                                value={selectedSetting}
                                                onChange={(e) => handleSettingSelect(e.target.value)}
                                                disabled={loading}
                                            >
                                                <option value=''>Choose a setting...</option>
                                                {settings.map((setting) => (
                                                    <option key={setting._id} value={setting._id}>
                                                        {setting.setting_code} {setting.city_id?.city_name ? `- ${setting.city_id.city_name}` : ''}
                                                    </option>
                                                ))}
                                            </Input>
                                            {loading && <small className='text-muted'>Loading settings...</small>}
                                        </div>

                                        {/* Admin Email Field */}
                                        <div className='mb-3'>
                                            <Label className='form-label'>Admin Email</Label>
                                            <Input
                                                type='email'
                                                placeholder='Enter admin email'
                                                value={adminEmail}
                                                onChange={(e) => setAdminEmail(e.target.value)}
                                                disabled={!selectedSetting}
                                            />
                                            <small className='text-muted'>
                                                Email address for admin notifications
                                            </small>
                                        </div>

                                        {/* Cron Time Field */}
                                        <div className='mb-3'>
                                            <Label className='form-label'>Cron Time</Label>
                                            <Input
                                                type='text'
                                                placeholder='Enter cron expression'
                                                value={cronTime}
                                                onChange={(e) => setCronTime(e.target.value)}
                                                disabled={!selectedSetting}
                                            />
                                            <small className='text-muted'>
                                                {cronFormatHelper}
                                            </small>
                                        </div>

                                        {/* Has Cron Radio Field */}
                                        <FormGroup className='mb-4'>
                                            <Label className='form-label'>Cron Job Status</Label>
                                            <div className='d-flex gap-4'>
                                                <div className='form-check'>
                                                    <Input
                                                        type='radio'
                                                        id='cronEnable'
                                                        name='hasCron'
                                                        checked={hasCron === true}
                                                        onChange={() => setHasCron(true)}
                                                        disabled={!selectedSetting}
                                                        className='form-check-input'
                                                    />
                                                    <Label className='form-check-label mr-2' for='cronEnable'>
                                                        Enable
                                                    </Label>
                                                </div>
                                                <div className='form-check'>
                                                    <Input
                                                        type='radio'
                                                        id='cronDisable'
                                                        name='hasCron'
                                                        checked={hasCron === false}
                                                        onChange={() => setHasCron(false)}
                                                        disabled={!selectedSetting}
                                                        className='form-check-input'
                                                    />
                                                    <Label className='form-check-label' for='cronDisable'>
                                                        Disable
                                                    </Label>
                                                </div>
                                            </div>
                                            <small className='text-muted'>
                                                Choose whether to enable or disable the scheduled cron job for automated tasks
                                            </small>
                                        </FormGroup>

                                        {/* Submit Button */}
                                        <div className='d-flex justify-content-end'>
                                            <Button
                                                type='submit'
                                                color='primary'
                                                disabled={updating || !selectedSetting}
                                            >
                                                {updating ? (
                                                    <>
                                                        <i className='ri-loader-4-line align-middle mr-1'></i>
                                                        Updating...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className='ri-save-line align-middle mr-1'></i>
                                                        Update Settings
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Information Column */}
                        <Col lg={4}>
                            <Card>
                                <CardBody>
                                    <h5 className='card-title'>About Settings</h5>
                                    <div className='mb-3'>
                                        <h6>Admin Email</h6>
                                        <p className='text-muted mb-0'>
                                            The email address where system notifications and alerts will be sent.
                                        </p>
                                    </div>
                                    <div className='mb-3'>
                                        <h6>Cron Time</h6>
                                        <p className='text-muted mb-0'>
                                            Cron expression for scheduling automated tasks. Uses standard cron format.
                                        </p>
                                    </div>
                                    <div className='mb-3'>
                                        <h6>Cron Job Status</h6>
                                        <p className='text-muted mb-0'>
                                            Choose "Enable" to activate the scheduled cron job or "Disable" to turn it off.
                                        </p>
                                    </div>
                                    <div>
                                        <h6>Common Cron Examples:</h6>
                                        <ul className='text-muted small ps-3 mb-0'>
                                            <li><code>0 8 * * *</code> - 8:00 AM daily</li>
                                            <li><code>0 20 * * *</code> - 8:00 PM daily</li>
                                        </ul>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                    {/* Shop Version Section */}
                    <Row>
                        <Col lg={8}>
                            <Card>
                                <CardBody>
                                    <h4 className='card-title mb-4'>Shop Version</h4>
                                    {shopVersionLoading ? (
                                        <p>Loading shop version...</p>
                                    ) : (
                                        <>
                                            {shopVersionMessage.text && (
                                                <Alert color={shopVersionMessage.type} className="mb-3">
                                                    {shopVersionMessage.text}
                                                </Alert>
                                            )}
                                            <Form onSubmit={handleShopVersionSubmit}>
                                                <div className='mb-3'>
                                                    <Label className='form-label'>Version</Label>
                                                    <Input
                                                        type='text'
                                                        placeholder='Enter version (e.g., 1.0.0)'
                                                        value={shopVersionInput}
                                                        onChange={(e) => setShopVersionInput(e.target.value)}
                                                        disabled={shopVersionUpdating}
                                                    />
                                                    <small className='text-muted'>
                                                        Semantic versioning format (major.minor.patch)
                                                    </small>
                                                </div>
                                                <div className='d-flex justify-content-end'>
                                                    <Button
                                                        type='submit'
                                                        color='primary'
                                                        disabled={shopVersionUpdating}
                                                    >
                                                        {shopVersionUpdating ? (
                                                            <>
                                                                <i className='ri-loader-4-line align-middle mr-1'></i>
                                                                Saving...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className='ri-save-line align-middle mr-1'></i>
                                                                {shopVersion ? 'Update Version' : 'Create Version'}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </Form>
                                        </>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            <Card>
                                <CardBody>
                                    <h5 className='card-title'>About Shop Version</h5>
                                    <p className='text-muted mb-0'>
                                        The shop version follows semantic versioning (major.minor.patch). It helps track
                                        releases and can be used displaying the current version in
                                        the Shop App.
                                    </p>
                                    {shopVersion && (
                                        <div className='mt-3'>
                                            <strong>Current Version:</strong> {shopVersion.version}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default GeneralSettings;